/**
 * MeetingCard Component
 * Displays a single meeting with date, time, type, status, and quick actions
 */

import {
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  FileText,
  FolderOpen,
  MapPin,
  Trash2,
  User,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  formatDuration,
  formatMeetingDate,
  formatMeetingTime,
  getMeetingStatusColor,
  getRelativeTimeString,
} from "@/lib/dateUtils";
import type { Meeting } from "@/types/meeting.types";

interface MeetingCardProps {
  meeting: Meeting;
  onEdit?: (meeting: Meeting) => void;
  onDelete?: (meeting: Meeting) => void;
  onComplete?: (meeting: Meeting) => void;
  onCancel?: (meeting: Meeting) => void;
  onClick?: (meeting: Meeting) => void;
  onViewCustomer?: (customerId: number) => void;
  onViewProject?: (projectId: number) => void;
  showActions?: boolean;
  showNavigation?: boolean; // Show View Customer / View Project buttons
}

export function MeetingCard({
  meeting,
  onEdit,
  onDelete,
  onComplete,
  onCancel,
  onClick,
  onViewCustomer,
  onViewProject,
  showActions = true,
  showNavigation = false,
}: MeetingCardProps) {
  const handleCardClick = () => {
    if (onClick) {
      onClick(meeting);
    }
  };

  const canComplete = meeting.status === "scheduled";
  const canCancel = meeting.status === "scheduled";

  return (
    <Card
      className={`group hover:shadow-md transition-all duration-200 ${
        onClick ? "cursor-pointer hover:border-primary/50" : ""
      }`}
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Header: Status & Type */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="font-medium capitalize">
              {meeting.event_type || "meeting"}
              {meeting.event_type === "meeting" && meeting.meeting_type && (
                <span className="text-muted-foreground text-sm ml-1">
                  · {meeting.meeting_type.replace("-", " ")}
                </span>
              )}
            </div>
            {meeting.duration_minutes > 0 && (
              <div className="text-xs text-muted-foreground">
                {formatDuration(meeting.duration_minutes)}
              </div>
            )}
          </div>
          <Badge className={getMeetingStatusColor(meeting.status)}>{meeting.status}</Badge>
        </div>

        {/* Date & Time */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{formatMeetingDate(meeting.meeting_date)}</span>
            <span className="text-muted-foreground">
              ({getRelativeTimeString(meeting.meeting_date)})
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{formatMeetingTime(meeting.meeting_time)}</span>
          </div>
        </div>

        {/* Location */}
        {meeting.location && (
          <div className="flex items-start gap-2 text-sm mb-3">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <span className="text-muted-foreground line-clamp-1">{meeting.location}</span>
          </div>
        )}

        {/* Notes Preview */}
        {meeting.notes && (
          <div className="flex items-start gap-2 text-sm mb-3">
            <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <span className="text-muted-foreground line-clamp-2">{meeting.notes}</span>
          </div>
        )}

        {/* Navigation Buttons */}
        {showNavigation && (onViewCustomer || (onViewProject && meeting.project_id)) && (
          <div className="flex gap-2 pb-3 border-b mb-3">
            {onViewCustomer && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewCustomer(meeting.customer_id);
                }}
                className="flex-1"
              >
                <User className="h-3.5 w-3.5 mr-1.5" />
                View Customer
              </Button>
            )}
            {onViewProject && meeting.project_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewProject(meeting.project_id);
                }}
                className="flex-1"
              >
                <FolderOpen className="h-3.5 w-3.5 mr-1.5" />
                View Project
              </Button>
            )}
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex gap-2 pt-3 border-t">
            {/* Primary Actions */}
            <div className="flex gap-2 flex-1">
              {canComplete && onComplete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete(meeting);
                  }}
                  className="flex-1"
                >
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                  Complete
                </Button>
              )}
              {canCancel && onCancel && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(meeting);
                  }}
                  className="flex-1"
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Cancel
                </Button>
              )}
            </div>

            {/* Edit & Delete */}
            <div className="flex gap-2">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(meeting);
                  }}
                  className="h-9 px-3"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(meeting);
                  }}
                  className="h-9 px-3 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
